import { useCallback, useEffect, useMemo } from 'react';

import { useStoreContext } from 'features/store';
import { useStorePosConnectivityStatus } from 'features/store/hooks/use-store-connectivity-status';

import { useCart } from 'hooks/use-cart';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { OrderStatus } from 'state/order';

import { useDialogActions } from './use-dialog-actions';
import { useRestaurantSearch } from './use-restaurant-search';

const useShouldShowPriceOrderFailureDialog = () => {
  const { store: activeStore } = useStoreContext();

  const { calculateCartTotalWithDiscount } = useCart();
  const {
    isRestaurantPosOnline,
    loading: isRestaurantPosConnectivityStatusLoading,
  } = useStorePosConnectivityStatus({
    storeId: activeStore.number || '',
  });

  const {
    serverOrder,
    unavailableCartEntries,
    priceOrderFailure,
    priceMutationError,
    orderStatus,
    shouldSkipPriceOrder,
  } = useCartContext();
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { isCartTotalNegative } = calculateCartTotalWithDiscount();
  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const isPriceLoading = isOrderPriceLoading || evaluatingIncentives;
  const hasPriceError = !!priceMutationError || priceOrderFailure;
  const errorConditions = useMemo(
    () => ({
      hasPricingErrors: hasPriceError && !isCartTotalNegative,
      hasConnectivityError: !isRestaurantPosOnline,
    }),
    [hasPriceError, isCartTotalNegative, isRestaurantPosOnline]
  );

  const shouldShowPriceOrderFailureDialog = useMemo(() => {
    return (
      // If there are unavailable items, we should show the unavailable items modal
      !isPriceLoading &&
      !isRestaurantPosConnectivityStatusLoading &&
      !unavailableCartEntries.length &&
      !shouldSkipPriceOrder &&
      (errorConditions.hasPricingErrors || errorConditions.hasConnectivityError)
    );
  }, [
    errorConditions.hasConnectivityError,
    errorConditions.hasPricingErrors,
    isPriceLoading,
    isRestaurantPosConnectivityStatusLoading,
    shouldSkipPriceOrder,
    unavailableCartEntries.length,
  ]);

  return shouldShowPriceOrderFailureDialog;
};

export const usePriceOrderFailureDialog = ({ onClose }: { onClose?: () => void }) => {
  const { store: activeStore } = useStoreContext();
  const {
    isLoading: isRestaurantSearchLoading,
    searchOneAvailableNearbyRestaurant,
    availableRestaurant,
  } = useRestaurantSearch(activeStore._id ?? undefined);
  const shouldShowPriceOrderFailureDialog = useShouldShowPriceOrderFailureDialog();
  const { isDialogOpen, selectNearbyStore, chooseAnotherStore, setIsDialogOpen } = useDialogActions(
    onClose,
    availableRestaurant ?? undefined
  );

  const openDialog = useCallback(async () => {
    await searchOneAvailableNearbyRestaurant();
    setIsDialogOpen(true);
  }, [searchOneAvailableNearbyRestaurant, setIsDialogOpen]);

  useEffect(() => {
    if (isDialogOpen || isRestaurantSearchLoading || !shouldShowPriceOrderFailureDialog) {
      return;
    }

    openDialog();
  }, [shouldShowPriceOrderFailureDialog, isRestaurantSearchLoading, openDialog, isDialogOpen]);

  return {
    shouldShowPriceOrderFailureDialog,
    isDialogOpen,
    selectNearbyStore,
    chooseAnotherStore,
    isRestaurantSearchLoading,
    availableRestaurant,
    setIsDialogOpen,
  };
};
